import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileClock, Send, Pencil, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const LABEL = { sales_invoice: "Sales invoice", purchase_bill: "Purchase bill", journal: "Journal", receipt: "Receipt", payment: "Payment" };
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

export const saveDraft = async (docType, payload, ui, id) => {
  const r = await api.post("/drafts", { doc_type: docType, payload, ui, id: id || undefined });
  toast.success("Saved as draft — nothing posted to the books yet");
  return r.data.id;
};

export const discardDraft = (id) => (id ? api.delete(`/drafts/${id}`).catch(() => {}) : Promise.resolve());

export const DraftsPanel = ({ docType, refreshKey, onOpen, onPosted }) => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/drafts", { params: { doc_type: docType } }).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, [docType, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const post = async (d) => {
    setBusy(d.id);
    try { await api.post(`/drafts/${d.id}/post`); toast.success(`${LABEL[d.doc_type]} posted`); load(); onPosted?.(); }
    catch (e) { const m = e?.response?.data?.detail; toast.error(typeof m === "string" ? m : "Post failed — open the draft to fix it"); }
    finally { setBusy(""); }
  };
  const remove = async (d) => {
    if (!window.confirm("Delete this draft?")) return;
    await api.delete(`/drafts/${d.id}`); toast.success("Draft deleted"); load();
  };

  if (!rows.length) return null;
  return (
    <div className="border border-amber-300/60 bg-amber-50/40 dark:bg-amber-500/5 rounded-sm p-3" data-testid={`drafts-panel-${docType.replace(",", "-")}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700 mb-2 flex items-center gap-1.5">
        <FileClock className="w-3.5 h-3.5" /> Drafts · not posted ({rows.length})
      </div>
      <div className="divide-y">
        {rows.map((d) => (
          <div key={d.id} className="flex flex-wrap items-center gap-3 py-1.5 text-xs" data-testid={`draft-row-${d.id}`}>
            <span className="font-medium w-28">{LABEL[d.doc_type]}</span>
            <span className="flex-1 min-w-[140px] truncate">{d.party_name || d.narration || "—"}</span>
            <span className="tabular-nums w-28 text-right">{inr(d.amount)}</span>
            <span className="text-muted-foreground w-40">saved {new Date(d.updated_at).toLocaleString("en-IN")}</span>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" className="h-7 rounded-sm gap-1" onClick={() => onOpen(d)} data-testid={`draft-open-${d.id}`}><Pencil className="w-3 h-3" /> Open</Button>
              <Button size="sm" className="h-7 rounded-sm gap-1 bg-accent text-white hover:bg-accent/90" disabled={busy === d.id} onClick={() => post(d)} data-testid={`draft-post-${d.id}`}><Send className="w-3 h-3" /> {busy === d.id ? "Posting…" : "Post"}</Button>
              <Button size="sm" variant="ghost" className="h-7 rounded-sm text-red-600" onClick={() => remove(d)} data-testid={`draft-delete-${d.id}`}><Trash2 className="w-3 h-3" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
